// src/constants/tradingView.ts

import type {
  TradingViewAdvancedChartConfig,
  TechnicalIndicator,
  TradingViewInterval,
} from "@/types/tradingView";

/**
 * TradingView Widget Constants and Default Configurations
 *
 * This file contains all the default configurations and constants
 * for TradingView widgets used throughout the application.
 */

// ==================== WIDGET SCRIPT URLS ====================

export const TRADINGVIEW_SCRIPTS = {
  ADVANCED_CHART:
    "https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js",
  TECHNICAL_ANALYSIS:
    "https://s3.tradingview.com/external-embedding/embed-widget-technical-analysis.js",
} as const;

// ==================== TECHNICAL INDICATOR MAPPINGS ====================

export const TRADINGVIEW_INDICATOR_STUDIES: Record<TechnicalIndicator, string> =
  {
    RSI: "RSI@tv-basicstudies",
    MACD: "MACD@tv-basicstudies",
    BB: "BB@tv-basicstudies",
  } as const;

// ==================== DEFAULT CONFIGURATIONS ====================

/**
 * Default configuration for Advanced Chart Widget
 * Optimized for crypto trading with dark theme
 */
export const DEFAULT_ADVANCED_CHART_CONFIG: TradingViewAdvancedChartConfig = {
  // Symbol and data
  symbol: "BINANCE:BTCUSDT",
  interval: "D",
  timezone: "Etc/UTC",

  // Appearance
  theme: "dark",
  style: "1", // Candlesticks
  locale: "en",
  backgroundColor: "#1f2937", // gray-800 to match our design
  gridColor: "rgba(242, 242, 242, 0.06)",

  // UI Controls - optimized for embedded use
  allow_symbol_change: false, // Start with false, can enable later
  hide_top_toolbar: false,
  hide_side_toolbar: true, // Hide to save space
  hide_legend: false,
  hide_volume: false,
  save_image: false, // Disable for embedded use

  // Features - minimal for initial implementation
  calendar: false,
  details: false,
  hotlist: false,
  withdateranges: false,
  autosize: true,

  // Advanced features - default includes moving average
  watchlist: [],
  compareSymbols: [],
  // MA20 (20-period Simple Moving Average) displayed by default
  // Format: StudyName@tv-basicstudies with input parameters [length]
  studies: [
    {
      id: "MASimple@tv-basicstudies",
      inputs: {
        length: 20, // MA20 instead of default MA9
      },
    },
  ],
  // Alternative fallback if object format not supported:
  // studies: ["MASimple@tv-basicstudies"], // Will need manual adjustment in UI

  // Initial zoom level - show 1 month of data for better candle visibility
  // Reduced from 3M to 1M to match the zoomed-in level of indicator charts
  range: "1M", // 1 month provides better zoom level for viewing MA lines clearly
};

/**
 * Default configuration for Technical Analysis Widget (indicators)
 * Used for RSI, MACD, etc. dedicated charts
 */
/**
 * Configuration for Advanced Chart Widget in indicator-only mode
 * This shows ONLY the indicator without the main price chart
 */
export const INDICATOR_ONLY_CHART_CONFIG: Omit<
  TradingViewAdvancedChartConfig,
  "studies"
> = {
  // Symbol and data
  symbol: "BINANCE:BTCUSDT",
  interval: "D",
  timezone: "Etc/UTC",

  // Appearance
  theme: "dark",
  style: "1", // Candlesticks (hidden anyway)
  locale: "en",
  backgroundColor: "#1f2937", // gray-800 to match our design
  gridColor: "rgba(242, 242, 242, 0.06)",

  // UI Controls - hide main chart UI elements
  allow_symbol_change: false,
  hide_top_toolbar: true, // Hide main toolbar
  hide_side_toolbar: true, // Hide side toolbar
  hide_legend: true, // Hide main chart legend
  hide_volume: true, // Hide volume
  save_image: false,

  // Features - minimal
  calendar: false,
  details: false,
  hotlist: false,
  withdateranges: false,
  autosize: true,

  // Advanced features
  watchlist: [],
  compareSymbols: [],
  // studies will be added per indicator
};

// ==================== SPECIFIC INDICATOR CONFIGS ====================

/**
 * Pre-configured setups for indicator-only Advanced Charts
 * These use the Advanced Chart Widget configured to show only indicators
 */
export const INDICATOR_ONLY_CONFIGS: Record<
  TechnicalIndicator,
  TradingViewAdvancedChartConfig
> = {
  RSI: {
    ...INDICATOR_ONLY_CHART_CONFIG,
    studies: [TRADINGVIEW_INDICATOR_STUDIES.RSI],
  },
  MACD: {
    ...INDICATOR_ONLY_CHART_CONFIG,
    studies: [TRADINGVIEW_INDICATOR_STUDIES.MACD],
  },
  BB: {
    ...INDICATOR_ONLY_CHART_CONFIG,
    studies: [TRADINGVIEW_INDICATOR_STUDIES.BB],
  },
} as const;

// ==================== RESPONSIVE BREAKPOINTS ====================

/**
 * Height configurations for different screen sizes
 * Matches our responsive design patterns
 */
export const CHART_HEIGHTS = {
  MAIN_CHART: {
    mobile: 300,
    tablet: 350,
    desktop: 400,
  },
  INDICATOR_CHART: {
    mobile: 150,
    tablet: 165,
    desktop: 180,
  },
} as const;

// ==================== ERROR MESSAGES ====================

export const TRADINGVIEW_ERROR_MESSAGES = {
  SCRIPT_LOAD_FAILED: "Failed to load TradingView widget script",
  CONTAINER_NOT_FOUND: "TradingView container element not found",
  INVALID_SYMBOL: "Invalid trading symbol provided",
  NETWORK_ERROR: "Network error while loading TradingView widget",
  CONFIG_ERROR: "Invalid TradingView widget configuration",
} as const;

// ==================== UTILITY CONSTANTS ====================

/**
 * CSS class names for TradingView widgets
 * Consistent naming for styling
 */
export const TRADINGVIEW_CSS_CLASSES = {
  CONTAINER: "tradingview-widget-container",
  WIDGET: "tradingview-widget-container__widget",
  LOADING: "tradingview-widget-loading",
  ERROR: "tradingview-widget-error",
} as const;

/**
 * Widget loading timeout (in milliseconds)
 */
export const WIDGET_LOAD_TIMEOUT = 10000; // 10 seconds

// ==================== INDICATOR AS SYMBOL MAPPINGS ====================

/**
 * Special TradingView symbols that allow charting an indicator directly
 * in the main pane, effectively making it full-screen.
 */
export const INDICATOR_AS_SYMBOL: Record<TechnicalIndicator, string> = {
  RSI: "TVC:RSI",
  MACD: "TVC:MACD",
  BB: "BINANCE:BTCUSDT", // Fallback
} as const;

export const TRADING_VIEW_TIME_RANGES: TradingViewInterval[] = [
  "1",
  "3",
  "5",
  "15",
  "30",
  "60",
  "240",
  "D",
  "W",
];
